import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ensureClipboardBridgeParam, useClipboardBridge } from '@/hooks/useClipboardBridge'

interface ClipboardEnabledIframeProps {
  /** The session URL to load in the iframe */
  sessionUrl: string | null
  /** Title for the iframe accessibility */
  title?: string
  /** Loading state from parent (e.g., waiting for session creation) */
  loading?: boolean
  /** Loading message to display */
  loadingMessage?: string
  /** Message to display when no session URL is available */
  noSessionMessage?: string
  /** Callback when iframe finishes loading */
  onLoad?: () => void
  /** External reload key to force iframe reload */
  externalReloadKey?: number
  /** Maximum number of auto-reload attempts (default: 2) */
  maxAutoReloadAttempts?: number
  /** Delay in ms between auto-reload attempts (default: 1500) */
  autoReloadDelayMs?: number
  /** Additional className for the iframe */
  className?: string
  /** Additional sandbox permissions beyond the defaults */
  additionalSandbox?: string
  /** Additional allow permissions beyond clipboard */
  additionalAllow?: string
  /**
   * Skip the clipboard bridge and load the raw sessionUrl. The bridge appends
   * a `clipboardBridge=true` param + injects a postMessage helper, which stalls
   * hyperbrowser's noVNC frame stream inside a sandboxed iframe (inbound_stall).
   * Disable it to render the viewer — at the cost of in-iframe clipboard paste.
   */
  disableClipboardBridge?: boolean
}

/**
 * Shared iframe component with clipboard bridge support and auto-reload logic.
 * Used across WorkflowExecutionPage, WorkflowExecutionDialog, BrowserDialog, and CreateBrowserProfileDialog.
 */
export function ClipboardEnabledIframe({
  sessionUrl,
  title = 'Session Viewer',
  loading = false,
  loadingMessage = 'Preparing session...',
  noSessionMessage = 'Session is not available.',
  onLoad,
  externalReloadKey,
  maxAutoReloadAttempts = 2,
  autoReloadDelayMs = 1500,
  className = 'h-full w-full border-0',
  additionalSandbox = '',
  additionalAllow = '',
  disableClipboardBridge = false,
}: ClipboardEnabledIframeProps) {
  const [internalReloadKey, setInternalReloadKey] = useState(0)
  const [iframeReady, setIframeReady] = useState(false)
  const [autoReloadAttempts, setAutoReloadAttempts] = useState(0)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Combine internal and external reload keys
  const effectiveReloadKey = externalReloadKey !== undefined
    ? externalReloadKey
    : internalReloadKey

  // Reset iframe state when session URL changes
  const prevSessionUrlRef = useRef<string | null>(null)
  useEffect(() => {
    if (sessionUrl && sessionUrl !== prevSessionUrlRef.current) {
      prevSessionUrlRef.current = sessionUrl
      setInternalReloadKey((prev) => prev + 1)
      setIframeReady(false)
      setAutoReloadAttempts(0)
    }
    if (!sessionUrl) {
      setIframeReady(false)
      setAutoReloadAttempts(0)
    }
  }, [sessionUrl])

  // Auto-reload logic if iframe hasn't loaded successfully
  useEffect(() => {
    if (!sessionUrl || iframeReady || loading) return
    if (autoReloadAttempts >= maxAutoReloadAttempts) return

    const timer = setTimeout(() => {
      setAutoReloadAttempts((prev) => prev + 1)
      setInternalReloadKey((prev) => prev + 1)
      setIframeReady(false)
    }, autoReloadDelayMs)

    return () => clearTimeout(timer)
  }, [sessionUrl, iframeReady, autoReloadAttempts, maxAutoReloadAttempts, autoReloadDelayMs, loading])

  // Use the raw session URL when the clipboard bridge is disabled — the bridge's
  // clipboardBridge=true param + postMessage helper stalls hyperbrowser's noVNC
  // frame stream inside a sandboxed iframe.
  const bridgedSessionUrl = useMemo(
    () =>
      disableClipboardBridge ? sessionUrl : ensureClipboardBridgeParam(sessionUrl),
    [sessionUrl, disableClipboardBridge]
  )

  // Enable clipboard bridge (skipped when disabled).
  useClipboardBridge({
    iframeRef,
    containerRef,
    enabled: Boolean(bridgedSessionUrl) && !disableClipboardBridge,
  })

  const handleIframeLoad = () => {
    setIframeReady(true)
    onLoad?.()
  }

  // Build sandbox attribute.
  // Clipboard permissions live in the `allow` attribute below — they are NOT
  // valid sandbox flags. Listing allow-clipboard-* here makes Chrome reject the
  // whole sandbox parse ("invalid sandbox flags"), which breaks embedded viewers
  // (notably hyperbrowser's noVNC page — the frame stays blank). Keep sandbox to
  // real flags only.
  const sandboxAttr = [
    'allow-same-origin',
    'allow-scripts',
    additionalSandbox,
  ]
    .filter(Boolean)
    .join(' ')

  // Build allow attribute
  const allowAttr = [
    'clipboard-read',
    'clipboard-write',
    additionalAllow,
  ]
    .filter(Boolean)
    .join('; ')

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingMessage}
        </div>
      </div>
    )
  }

  if (!bridgedSessionUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-muted-foreground text-sm">{noSessionMessage}</div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="h-full w-full">
      <iframe
        ref={iframeRef}
        title={title}
        src={bridgedSessionUrl}
        sandbox={sandboxAttr}
        allow={allowAttr}
        className={className}
        key={effectiveReloadKey}
        onLoad={handleIframeLoad}
      />
    </div>
  )
}
